import React, { useEffect, useState, type DragEvent } from 'react';
import { hasNestedJsx } from '../lib/fieldChecks.ts';
import { usePersistedState } from '../lib/persistedState.ts';
import { listGroupLabel } from '../lib/optionLabels.ts';
import { FieldControl, CompactField, LockedField, MediaRow } from './FieldControls.tsx';
import type { EditableField, ListEntry, ListGroup, MediaField } from '../../shared/deck.ts';
import { ChevronDown, ChevronRight, ChevronUp, Drag, Trash } from '../lib/icons.tsx';

/**
 * The Edit tab's grouped LIST sections: one collapsible card per list entry
 * (agenda sections, cards, people, chart data points, table rows) with
 * add/remove and drag-handle reordering. Extracted from EditPanel.tsx —
 * the panel renders one <ListGroupSection> per list the server reports.
 */

/** Which entry is in flight, and where it would land. `over` is null until
 *  the pointer has crossed a card. */
type DragState = { from: number; over: number | null; after: boolean };

/** Blur-to-save handler, matching FieldControls' own. */
type SaveField = (fieldId: string, value: string) => void;

/** Remap an expanded-index set after a move/remove so open cards follow
 *  their items. */
function remapAfterMove(set: ReadonlySet<number>, from: number, to: number): Set<number> {
  const out = new Set<number>();
  for (const i of set) {
    if (i === from) out.add(to);
    else if (from < to && i > from && i <= to) out.add(i - 1);
    else if (to < from && i >= to && i < from) out.add(i + 1);
    else out.add(i);
  }
  return out;
}
function remapAfterRemove(set: ReadonlySet<number>, removed: number): Set<number> {
  const out = new Set<number>();
  for (const i of set) {
    if (i === removed) continue;
    out.add(i > removed ? i - 1 : i);
  }
  return out;
}

/** One list on the slide: header (label · count · add), then item cards
 *  (collapsed by default, drag-to-reorder), compact rows (chart data) or a
 *  cell grid (table rows). */
type ListGroupSectionProps = {
  slug: string;
  group: ListGroup;
  fieldById: Map<string, EditableField>;
  mediaById: Map<string, MediaField>;
  saving?: boolean;
  onSaveField: SaveField;
  /** Rejects on a conflict; the panel's banner shows the message. */
  onListOp: (listId: string, op: string, args?: Record<string, number>) => Promise<unknown>;
  onPickMedia: (field: MediaField) => void;
  onAskInChat?: (prefill: string) => void;
};

export default function ListGroupSection({
  slug,
  group,
  fieldById,
  mediaById,
  saving,
  onSaveField,
  onListOp,
  onPickMedia,
  onAskInChat,
}: ListGroupSectionProps) {
  const [open, setOpen] = usePersistedState(`studio.edit.group.list:${group.id}`, true);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [drag, setDrag] = useState<DragState | null>(null);

  async function add() {
    const newIndex = group.entries.length;
    try {
      await onListOp(group.id, 'add');
      setExpanded((s) => new Set([...s, newIndex]));
    } catch {
      /* conflict banner shows the message */
    }
  }
  async function remove(index: number) {
    try {
      await onListOp(group.id, 'remove', { index });
      setExpanded((s) => remapAfterRemove(s, index));
    } catch {
      /* conflict banner */
    }
  }
  async function move(from: number, to: number) {
    if (from === to) return;
    try {
      await onListOp(group.id, 'move', { from, to });
      setExpanded((s) => remapAfterMove(s, from, to));
    } catch {
      /* conflict banner */
    }
  }

  function dropTarget(): number | null {
    if (!drag || drag.over == null) return null;
    const insertion = drag.over + (drag.after ? 1 : 0);
    return insertion > drag.from ? insertion - 1 : insertion;
  }
  function onDrop() {
    const from = drag?.from;
    const to = dropTarget();
    setDrag(null);
    if (to != null && from != null && to !== from) move(from, to);
  }

  const dragHandlers = (index: number): Pick<React.ComponentProps<'div'>, 'onDragOver' | 'onDrop'> => ({
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      if (!drag) return;
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      const after = e.clientY > r.top + r.height / 2;
      setDrag((d) => (d && (d.over !== index || d.after !== after) ? { ...d, over: index, after } : d));
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      onDrop();
    },
  });

  const body =
    group.display === 'table' ? (
      <TableGrid group={group} fieldById={fieldById} saving={saving} onSaveField={onSaveField} onRemove={remove} />
    ) : group.display === 'compact' ? (
      <div className="edit-list__rows">
        {group.entries.map((entry) => (
          <div key={entry.index} className="edit-list__row">
            {entry.fieldIds.map((id) => {
              const f = fieldById.get(id);
              return f ? <CompactField key={id} field={f} onSave={onSaveField} /> : null;
            })}
            {group.canRemove && <RemoveButton compact onConfirm={() => remove(entry.index)} disabled={saving} />}
          </div>
        ))}
      </div>
    ) : (
      group.entries.map((entry) => (
        <ItemCard
          key={entry.index}
          slug={slug}
          group={group}
          entry={entry}
          fieldById={fieldById}
          mediaField={entry.mediaFieldId ? mediaById.get(entry.mediaFieldId) : null}
          expanded={expanded.has(entry.index)}
          onToggle={() =>
            setExpanded((s) => {
              const out = new Set(s);
              if (out.has(entry.index)) out.delete(entry.index);
              else out.add(entry.index);
              return out;
            })
          }
          saving={saving}
          onSaveField={onSaveField}
          onPickMedia={onPickMedia}
          onRemove={() => remove(entry.index)}
          onMoveUp={entry.index > 0 ? () => move(entry.index, entry.index - 1) : null}
          onMoveDown={entry.index < group.entries.length - 1 ? () => move(entry.index, entry.index + 1) : null}
          drag={drag}
          setDrag={setDrag}
          dragHandlers={group.canReorder ? dragHandlers(entry.index) : {}}
          onAskInChat={onAskInChat}
        />
      ))
    );

  return (
    <section className="edit-panel__group edit-list" onDragEnd={() => setDrag(null)}>
      <div className="edit-list__head">
        {/* Same collapsible header as the panel's other sections (EditPanel's
            Group) — a list is a section like any other, so it folds too. The
            Add button stays outside the toggle: it acts on the list, not on
            the disclosure. */}
        <button
          type="button"
          className="edit-panel__toggle"
          aria-expanded={open ? 'true' : 'false'}
          onClick={() => setOpen((v) => !v)}
        >
          <svg className="edit-panel__toggle-caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
            <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {listGroupLabel(group.label)} <span className="edit-list__count">{group.entries.length}</span>
        </button>
        {group.canAdd && open && (
          <button type="button" className="btn btn--ghost btn--sm edit-list__add" onClick={add} disabled={saving}>
            + Add
          </button>
        )}
      </div>
      {open && body}
    </section>
  );
}

/** One list entry as a collapsible card: grip · number · title · chevron;
 *  expanded → its text fields, media row and remove/reorder controls. */
type ItemCardProps = {
  slug: string;
  group: ListGroup;
  entry: ListEntry;
  fieldById: Map<string, EditableField>;
  /** Absent on entries with no image, and undefined when the id no longer
   *  resolves after a CRUD op reshuffled the payload. */
  mediaField?: MediaField | null;
  expanded: boolean;
  onToggle: () => void;
  saving?: boolean;
  onSaveField: SaveField;
  onPickMedia: (field: MediaField) => void;
  onRemove: () => void;
  /** Null at the ends of the list. */
  onMoveUp?: (() => void) | null;
  onMoveDown?: (() => void) | null;
  drag: DragState | null;
  setDrag: (drag: DragState) => void;
  /** Empty when the list can't be reordered. */
  dragHandlers: Pick<React.ComponentProps<'div'>, 'onDragOver' | 'onDrop'>;
  onAskInChat?: (prefill: string) => void;
};

function ItemCard({
  slug,
  group,
  entry,
  fieldById,
  mediaField,
  expanded,
  onToggle,
  saving,
  onSaveField,
  onPickMedia,
  onRemove,
  onMoveUp,
  onMoveDown,
  drag,
  setDrag,
  dragHandlers,
  onAskInChat,
}: ItemCardProps) {
  // The card is draggable only while the pointer is on the grip — text
  // selection and input drags inside the body stay native.
  const [armed, setArmed] = useState(false);
  const isDragging = drag && drag.from === entry.index;
  const target = drag && drag.over === entry.index;
  const dropBefore = target && !drag.after;
  const dropAfter = target && drag.after;

  const entryFields = entry.fieldIds.map((id) => fieldById.get(id)).filter((f): f is EditableField => f !== undefined);

  return (
    <div
      className={
        'item-card' +
        (isDragging ? ' item-card--dragging' : '') +
        (dropBefore ? ' item-card--drop-before' : '') +
        (dropAfter ? ' item-card--drop-after' : '')
      }
      draggable={armed}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(entry.index));
        setDrag({ from: entry.index, over: null, after: false });
      }}
      {...dragHandlers}
    >
      <div className="item-card__head" onClick={onToggle}>
        {group.canReorder && (
          <span
            className="item-card__grip"
            title="Drag to reorder"
            onMouseDown={(e) => {
              e.stopPropagation();
              setArmed(true);
            }}
            onMouseUp={() => setArmed(false)}
            onMouseLeave={() => setArmed(false)}
            onClick={(e) => e.stopPropagation()}
            aria-hidden="true"
          >
            <Drag />
          </span>
        )}
        <span className="item-card__index">{String(entry.index + 1).padStart(2, '0')}</span>
        <span className="item-card__label">{entry.label}</span>
        <span className="item-card__chevron" aria-hidden="true">
          {expanded ? <ChevronDown /> : <ChevronRight />}
        </span>
      </div>
      {expanded && (
        <div className="item-card__body">
          {mediaField && (
            <MediaRow slug={slug} field={mediaField} disabled={saving} onReplace={() => onPickMedia(mediaField)} />
          )}
          {entryFields.map((f) =>
            hasNestedJsx(f.value) ? (
              <LockedField key={f.id} field={f} onAskInChat={onAskInChat} />
            ) : (
              <FieldControl key={f.id} field={f} onSave={onSaveField} />
            ),
          )}
          <div className="item-card__actions">
            {(onMoveUp || onMoveDown) && (
              <span className="item-card__movers">
                <button
                  type="button"
                  className="iconbtn"
                  onClick={onMoveUp || undefined}
                  disabled={!onMoveUp || saving}
                  aria-label="Move up"
                >
                  <ChevronUp />
                </button>
                <button
                  type="button"
                  className="iconbtn"
                  onClick={onMoveDown || undefined}
                  disabled={!onMoveDown || saving}
                  aria-label="Move down"
                >
                  <ChevronDown />
                </button>
              </span>
            )}
            {group.canRemove && <RemoveButton onConfirm={onRemove} disabled={saving} />}
          </div>
        </div>
      )}
    </div>
  );
}

/** Two-tap remove: "Remove" → "Sure?" — no modal, no accidental deletes. */
function RemoveButton({
  onConfirm,
  disabled,
  compact,
}: {
  onConfirm: () => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [arming, setArming] = useState(false);
  useEffect(() => {
    if (!arming) return undefined;
    const tm = setTimeout(() => setArming(false), 2500);
    return () => clearTimeout(tm);
  }, [arming]);
  return (
    <button
      type="button"
      className={'btn btn--ghost btn--sm edit-list__remove' + (arming ? ' edit-list__remove--arming' : '')}
      onClick={() => (arming ? onConfirm() : setArming(true))}
      disabled={disabled}
      aria-label={compact ? 'Remove entry' : undefined}
    >
      {arming ? 'Sure?' : compact ? <Trash /> : 'Remove'}
    </button>
  );
}

/** Table rows as a cell grid: header from the field labels ("row 1 · Drink"
 *  → "Drink"), one input per cell, a remove button per row. */
function TableGrid({
  group,
  fieldById,
  saving,
  onSaveField,
  onRemove,
}: {
  group: ListGroup;
  fieldById: Map<string, EditableField>;
  saving?: boolean;
  onSaveField: SaveField;
  onRemove: (index: number) => void;
}) {
  const rows = group.entries.map((e) =>
    e.fieldIds.map((id) => fieldById.get(id)).filter((f): f is EditableField => f !== undefined),
  );
  const colCount = Math.max(0, ...rows.map((r) => r.length));
  const headers = (rows[0] || []).map((f) => {
    const parts = (f.label || '').split('·');
    return (parts[1] || parts[0] || '').trim();
  });

  return (
    <div
      className="edit-table"
      style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)${group.canRemove ? ' auto' : ''}` }}
    >
      {headers.map((h, i) => (
        <span key={`h${i}`} className="edit-table__header">
          {h}
        </span>
      ))}
      {group.canRemove && <span className="edit-table__header" />}
      {group.entries.map((entry, r) => {
        // `rows` is built by mapping `entries`, so this is never the fallback.
        const row = rows[r] ?? [];
        return (
          <React.Fragment key={entry.index}>
            {row.map((f) => (
              <CompactField key={f.id} field={f} onSave={onSaveField} bare />
            ))}
            {row.length < colCount &&
              Array.from({ length: colCount - row.length }).map((_, i) => <span key={`pad${i}`} />)}
            {group.canRemove && <RemoveButton compact onConfirm={() => onRemove(entry.index)} disabled={saving} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
